/**
 * UI Store (Zustand)
 *
 * Manages purely local UI state that doesn't need server sync.
 * This is separate from session-bound data which uses TanStack Query.
 *
 * State categories:
 * - UI preferences (sidebar, panels)
 * - Draft content (unsent messages)
 * - UI expansion state (collapsed sections)
 *
 * @module stores/uiStore
 */

import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';

/**
 * UI Store
 *
 * Persisted to localStorage automatically via zustand/persist.
 * DevTools integration for debugging.
 */
const useUIStore = create(
  devtools(
    persist(
      (set, get) => ({
        // ============================================
        // State
        // ============================================

        /** Sidebar collapsed state */
        sidebarCollapsed: false,

        /** Chat panel visible state */
        chatPanelVisible: true,

        /** Currently selected panel in sidebar */
        selectedPanel: 'agents', // 'agents' | 'chat' | 'tasks' | 'settings'

        /** Chat input drafts per agent (unsent messages) */
        chatInputDrafts: {},

        /** Expanded/collapsed state for collapsible sections */
        expandedSections: {
          agentList: true,
          taskList: true,
          chatChannels: true,
        },

        /** Theme preference (respects system if 'system') */
        theme: 'system', // 'light' | 'dark' | 'system'

        /** Last selected workspace session ID (for restoring on reload) */
        lastWorkspaceSessionId: null,

        /** Raw mode enabled per agent (debug view showing JSON) - NOT persisted */
        rawModeAgents: {},

        /** Whisper mode per agent ('enabled' | 'disabled' | 'focus') - NOT persisted */
        whisperModeAgents: {},

        // ============================================
        // Actions
        // ============================================

        /**
         * Toggle sidebar collapsed state
         */
        toggleSidebar: () =>
          set(
            (state) => ({ sidebarCollapsed: !state.sidebarCollapsed }),
            false,
            'toggleSidebar'
          ),

        /**
         * Set sidebar collapsed state
         * @param {boolean} collapsed
         */
        setSidebarCollapsed: (collapsed) =>
          set({ sidebarCollapsed: collapsed }, false, 'setSidebarCollapsed'),

        /**
         * Toggle chat panel visibility
         */
        toggleChatPanel: () =>
          set(
            (state) => ({ chatPanelVisible: !state.chatPanelVisible }),
            false,
            'toggleChatPanel'
          ),

        /**
         * Set chat panel visibility
         * @param {boolean} visible
         */
        setChatPanelVisible: (visible) =>
          set({ chatPanelVisible: visible }, false, 'setChatPanelVisible'),

        /**
         * Set selected panel
         * @param {'agents' | 'chat' | 'tasks' | 'settings'} panel
         */
        setSelectedPanel: (panel) =>
          set({ selectedPanel: panel }, false, 'setSelectedPanel'),

        /**
         * Set chat draft for an agent
         * @param {string} agentId
         * @param {string} text
         */
        setChatDraft: (agentId, text) =>
          set(
            (state) => ({
              chatInputDrafts: { ...state.chatInputDrafts, [agentId]: text },
            }),
            false,
            'setChatDraft'
          ),

        /**
         * Get chat draft for an agent
         * @param {string} agentId
         * @returns {string}
         */
        getChatDraft: (agentId) => get().chatInputDrafts[agentId] || '',

        /**
         * Clear chat draft for an agent (after sending)
         * @param {string} agentId
         */
        clearChatDraft: (agentId) =>
          set(
            (state) => {
              const { [agentId]: _, ...rest } = state.chatInputDrafts;
              return { chatInputDrafts: rest };
            },
            false,
            'clearChatDraft'
          ),

        /**
         * Toggle section expanded state
         * @param {string} sectionId
         */
        toggleSection: (sectionId) =>
          set(
            (state) => ({
              expandedSections: {
                ...state.expandedSections,
                [sectionId]: !state.expandedSections[sectionId],
              },
            }),
            false,
            'toggleSection'
          ),

        /**
         * Set section expanded state
         * @param {string} sectionId
         * @param {boolean} expanded
         */
        setSectionExpanded: (sectionId, expanded) =>
          set(
            (state) => ({
              expandedSections: {
                ...state.expandedSections,
                [sectionId]: expanded,
              },
            }),
            false,
            'setSectionExpanded'
          ),

        /**
         * Check if section is expanded
         * @param {string} sectionId
         * @returns {boolean}
         */
        isSectionExpanded: (sectionId) =>
          get().expandedSections[sectionId] ?? true,

        /**
         * Set theme preference
         * @param {'light' | 'dark' | 'system'} theme
         */
        setTheme: (theme) => set({ theme }, false, 'setTheme'),

        /**
         * Set last workspace session ID
         * @param {string | null} sessionId
         */
        setLastWorkspaceSessionId: (sessionId) =>
          set(
            { lastWorkspaceSessionId: sessionId },
            false,
            'setLastWorkspaceSessionId'
          ),

        /**
         * Toggle raw mode for an agent (debug view showing JSON)
         * @param {string} agentId
         */
        toggleRawMode: (agentId) =>
          set(
            (state) => ({
              rawModeAgents: {
                ...state.rawModeAgents,
                [agentId]: !state.rawModeAgents[agentId],
              },
            }),
            false,
            'toggleRawMode'
          ),

        /**
         * Check if raw mode is enabled for an agent
         * @param {string} agentId
         * @returns {boolean}
         */
        isRawMode: (agentId) => get().rawModeAgents[agentId] || false,

        /**
         * Set whisper mode for an agent
         * @param {string} agentId
         * @param {'enabled' | 'disabled' | 'focus'} mode
         */
        setWhisperMode: (agentId, mode) =>
          set(
            (state) => ({
              whisperModeAgents: {
                ...state.whisperModeAgents,
                [agentId]: mode,
              },
            }),
            false,
            'setWhisperMode'
          ),

        /**
         * Get whisper mode for an agent (default: 'enabled')
         * @param {string} agentId
         * @returns {'enabled' | 'disabled' | 'focus'}
         */
        getWhisperMode: (agentId) => get().whisperModeAgents[agentId] || 'enabled',

        /**
         * Reset UI state to defaults (for testing/debugging)
         */
        resetUI: () =>
          set(
            {
              sidebarCollapsed: false,
              chatPanelVisible: true,
              selectedPanel: 'agents',
              chatInputDrafts: {},
              expandedSections: {
                agentList: true,
                taskList: true,
                chatChannels: true,
              },
              theme: 'system',
              rawModeAgents: {},
              whisperModeAgents: {},
            },
            false,
            'resetUI'
          ),
      }),
      {
        name: 'claude-webui-ui-store',
        version: 1,
        // Only persist certain fields
        partialize: (state) => ({
          sidebarCollapsed: state.sidebarCollapsed,
          chatPanelVisible: state.chatPanelVisible,
          selectedPanel: state.selectedPanel,
          expandedSections: state.expandedSections,
          theme: state.theme,
          lastWorkspaceSessionId: state.lastWorkspaceSessionId,
          // Note: chatInputDrafts are NOT persisted (they're ephemeral)
        }),
      }
    ),
    { name: 'UIStore' }
  )
);

export default useUIStore;
